import React, { useMemo, useRef, Suspense, useState, useEffect } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import {
    MeshTransmissionMaterial,
    Environment,
    PerspectiveCamera,
    Float,
    Center
} from '@react-three/drei';
import * as THREE from 'three';
import { motion, AnimatePresence } from 'framer-motion';

// --- Custom Oval Path for Chain Links ---
class StadiumCurve extends THREE.Curve {
    constructor(length = 0.2, width = 0.15) {
        super();
        this.length = length;
        this.width = width;
    }
    getPoint(t) {
        const fullRange = 2 * this.length + 2 * Math.PI * this.width;
        const dist = t * fullRange;

        if (dist < this.length) {
            return new THREE.Vector3(this.width, dist - this.length / 2, 0);
        } else if (dist < this.length + Math.PI * this.width) {
            const angle = (dist - this.length) / this.width;
            return new THREE.Vector3(Math.cos(angle) * this.width, this.length / 2 + Math.sin(angle) * this.width, 0);
        } else if (dist < 2 * this.length + Math.PI * this.width) {
            return new THREE.Vector3(-this.width, this.length / 2 - (dist - (this.length + Math.PI * this.width)), 0);
        } else {
            const angle = Math.PI + (dist - (2 * this.length + Math.PI * this.width)) / this.width;
            return new THREE.Vector3(Math.cos(angle) * this.width, -this.length / 2 + Math.sin(angle) * this.width, 0);
        }
    }
}

// --- WebGL 3D Chain Link ---
const GlassLink = ({ position, rotation, index }) => {
    const meshRef = useRef();

    // Custom geometry for elongated oval chain link
    const ovalGeometry = useMemo(() => {
        const curve = new StadiumCurve(0.25, 0.12); // length, width
        return new THREE.TubeGeometry(curve, 64, 0.075, 24, true);
    }, []);

    // Transmission material settings for "Pure Colorless Optical Crystal"
    const config = {
        backside: true,
        backsideThickness: 0.3,
        samples: 32,
        resolution: 1024,
        transmission: 1.0,
        roughness: 0.0,
        thickness: 0.5,
        ior: 1.9,
        chromaticAberration: 0.02,
        anisotropy: 1.0,
        distortion: 0.0,
        distortionScale: 0.1,
        temporalDistortion: 0.0,
        clearcoat: 1,
        clearcoatRoughness: 0,
        attenuationDistance: 10.0,
        attenuationColor: '#ffffff',
        color: '#ffffff',
    };

    return (
        <mesh ref={meshRef} position={position} rotation={rotation} castShadow geometry={ovalGeometry}>
            <MeshTransmissionMaterial {...config} />
        </mesh>
    );
};

// --- Organic Spline Chain Component ---
const SplineChain = ({ count = 36, id, animationMode = 1 }) => {
    const groupRef = useRef();

    const initialPoints = useMemo(() => {
        const p = [];
        const xOffset = (id - 1) * 4;
        for (let i = 0; i < 6; i++) {
            p.push(new THREE.Vector3(
                xOffset,
                i * 3.5 - 9,
                0
            ));
        }
        return p;
    }, [id]);

    const curve = useMemo(() => new THREE.CatmullRomCurve3(initialPoints), [initialPoints]);
    const linksArray = useMemo(() => new Array(count).fill(0), [count]);

    useFrame((state) => {
        if (!groupRef.current) return;
        const t = state.clock.getElapsedTime();

        // --- Multi-Mode Physics Engine ---
        curve.points.forEach((p, i) => {
            const lag = i * 0.6;
            const speed = 0.8;
            const amp = 0.4 + i * 0.2;

            if (animationMode === 1) {
                // VORTEX (Spiraling)
                const radius = 2.0 + i * 0.5;
                const angle = t * speed - i * 0.5 + (id * Math.PI * 0.6);
                p.x = Math.cos(angle) * radius;
                p.z = Math.sin(angle) * (radius * 0.5);
                p.y = initialPoints[i].y + Math.sin(t * speed + i) * 0.5;
            } else if (animationMode === 2) {
                // SLITHER (Organic Snake)
                const snakeSpeed = 1.2;
                p.x = initialPoints[i].x + Math.sin(t * snakeSpeed - lag + id) * (amp * 1.5);
                p.z = Math.cos(t * snakeSpeed * 0.8 - lag * 1.2 + id) * (amp * 1.2);
                p.y = initialPoints[i].y + Math.sin(t * 0.5 + i) * 0.3;
            } else {
                // SWING (Heavy Pendulum)
                const swingSpeed = 0.6;
                const swingAmp = 1.5 + i * 0.8;
                p.x = initialPoints[i].x + Math.sin(t * swingSpeed - i * 0.2 + id) * swingAmp;
                p.z = Math.cos(t * swingSpeed * 0.5 - i * 0.1) * (swingAmp * 0.3);
            }
        });
        curve.updateArcLengths();

        const children = groupRef.current.children;
        const spacing = 0.025; // Tightened spacing for interlocking connection

        children.forEach((mesh, i) => {
            const fraction = i * spacing;
            if (fraction > 1) {
                mesh.visible = false;
                return;
            }
            mesh.visible = true;

            const pos = curve.getPointAt(fraction);
            const tangent = curve.getTangentAt(fraction);

            mesh.position.copy(pos);
            const up = new THREE.Vector3(0, 1, 0);
            const axis = new THREE.Vector3().crossVectors(up, tangent).normalize();
            const radians = Math.acos(up.dot(tangent));
            mesh.quaternion.setFromAxisAngle(axis, radians);

            // Alternating 90-degree rotation for real chain interlocking
            if (i % 2 === 0) {
                mesh.rotateOnAxis(new THREE.Vector3(0, 1, 0), Math.PI / 2);
            }
        });
    });

    return (
        <group ref={groupRef}>
            {linksArray.map((_, i) => (
                <GlassLink key={i} index={i} />
            ))}
        </group>
    );
};

// --- Grained Nebula Background Shader ---
const GrainedBackground = () => {
    const meshRef = useRef();

    // Custom Shader for Grained Gradient Nebula
    const shaderArgs = useMemo(() => ({
        uniforms: {
            uTime: { value: 0 },
            uColorA: { value: new THREE.Color('#020617') }, // Deep Dark
            uColorB: { value: new THREE.Color('#1e3a8a') }, // Cobalt
            uColorC: { value: new THREE.Color('#2966F4') }, // Brand Blue
        },
        vertexShader: `
            varying vec2 vUv;
            void main() {
                vUv = uv;
                gl_Position = vec4(position, 1.0);
            }
        `,
        fragmentShader: `
            varying vec2 vUv;
            uniform float uTime;
            uniform vec3 uColorA;
            uniform vec3 uColorB;
            uniform vec3 uColorC;

            // Pseudo-random for grain
            float random(vec2 st) {
                return fract(sin(dot(st.xy, vec2(12.9898,78.233))) * 43758.5453123);
            }

            // Simple noise for nebula shapes
            float noise(vec2 p) {
                vec2 i = floor(p);
                vec2 f = fract(p);
                f = f * f * (3.0 - 2.0 * f);
                float a = random(i + vec2(0.0, 0.0));
                float b = random(i + vec2(1.0, 0.0));
                float c = random(i + vec2(0.0, 1.0));
                float d = random(i + vec2(1.0, 1.0));
                return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
            }

            void main() {
                vec2 uv = vUv;
                float t = uTime * 0.1;
                
                // Layered noise for nebula feel
                float n = noise(uv * 3.0 + t);
                n += noise(uv * 6.0 - t * 0.5) * 0.5;
                
                // Blend colors based on noise and UV
                vec3 color = mix(uColorA, uColorB, n);
                color = mix(color, uColorC, uv.y * 0.5 + n * 0.3);
                
                // Add Cinematic Grain
                float grain = (random(uv + fract(uTime)) - 0.5) * 0.06;
                color += grain;
                
                gl_FragColor = vec4(color, 0.2); // Very subtle (V19)
            }
        `
    }), []);

    useFrame((state) => {
        if (meshRef.current) {
            meshRef.current.material.uniforms.uTime.value = state.clock.getElapsedTime();
        }
    });

    return (
        <mesh ref={meshRef} position={[0, 0, -20]}>
            <planeGeometry args={[2, 2]} />
            <shaderMaterial
                args={[shaderArgs]}
                transparent={true}
                depthWrite={false}
                depthTest={false}
            />
        </mesh>
    );
};

// --- Twinkling Star Field ---
const TwinklingStars = () => {
    const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
    const count = isMobile ? 40 : 100;

    // Generate static positions and random sparkle offsets
    const [positions, offsets] = useMemo(() => {
        const pos = new Float32Array(count * 3);
        const off = new Float32Array(count);
        for (let i = 0; i < count; i++) {
            pos[i * 3] = (Math.random() - 0.5) * 20;
            pos[i * 3 + 1] = (Math.random() - 0.5) * 15;
            pos[i * 3 + 2] = (Math.random() - 0.5) * 10 - 15; // Set deep in background
            off[i] = Math.random() * 100; // Random offset for individual twinkle heartbeat
        }
        return [pos, off];
    }, [count]);

    const shaderArgs = useMemo(() => ({
        uniforms: {
            uTime: { value: 0 },
        },
        vertexShader: `
            attribute float aOffset;
            varying float vTwinkle;
            uniform float uTime;
            void main() {
                vTwinkle = sin(uTime * (1.5 + mod(aOffset, 2.0)) + aOffset);
                vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
                gl_PointSize = 2.5 * (1.0 / -mvPosition.z); // Very small scale
                gl_Position = projectionMatrix * mvPosition;
            }
        `,
        fragmentShader: `
            varying float vTwinkle;
            void main() {
                float opacity = smoothstep(-1.0, 1.0, vTwinkle) * 0.8 + 0.2;
                gl_FragColor = vec4(1.0, 1.0, 1.0, opacity);
            }
        `
    }), []);

    useFrame((state) => {
        shaderArgs.uniforms.uTime.value = state.clock.getElapsedTime();
    });

    return (
        <points>
            <bufferGeometry>
                <bufferAttribute
                    attach="attributes-position"
                    count={count}
                    array={positions}
                    itemSize={3}
                />
                <bufferAttribute
                    attach="attributes-aOffset"
                    count={count}
                    array={offsets}
                    itemSize={1}
                />
            </bufferGeometry>
            <shaderMaterial
                args={[shaderArgs]}
                transparent
                depthWrite={false}
            />
        </points>
    );
};

// --- Graphical Network Component ---
const GraphicalNetwork = ({ count = 100 }) => {
    const pointsRef = useRef();
    const linesRef = useRef();

    // Node data: positions and velocities
    const nodes = useMemo(() => {
        const p = [];
        for (let i = 0; i < count; i++) {
            p.push({
                // Start WIDE for a star-field feel (NO Big Bang)
                basePos: new THREE.Vector3((Math.random() - 0.5) * 12, (Math.random() - 0.5) * 8, (Math.random() - 0.5) * 8),
                pos: new THREE.Vector3(),
                // Ultra-Slow Cosmic Drift
                vel: new THREE.Vector3((Math.random() - 0.5) * 0.003, (Math.random() - 0.5) * 0.003, (Math.random() - 0.5) * 0.003)
            });
        }
        return p;
    }, [count]);

    // Custom Shader for Glowing Star Particles
    const starShaderArgs = useMemo(() => ({
        uniforms: {
            uTime: { value: 0 },
        },
        vertexShader: `
            varying float vOpacity;
            uniform float uTime;
            void main() {
                // Subtle individual star pulse
                float pulse = 0.8 + 0.2 * sin(uTime * 2.0 + position.x * 10.0);
                vOpacity = pulse;
                
                vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
                gl_PointSize = 4.0 * (10.0 / -mvPosition.z); // Slightly larger for glow
                gl_Position = projectionMatrix * mvPosition;
            }
        `,
        fragmentShader: `
            varying float vOpacity;
            void main() {
                // Create a soft radial glow (Circular)
                float dist = distance(gl_PointCoord, vec2(0.5));
                if (dist > 0.5) discard;
                
                float glow = pow(1.0 - dist * 2.0, 2.0);
                gl_FragColor = vec4(1.0, 1.0, 1.0, glow * vOpacity * 0.9);
            }
        `
    }), []);

    useFrame((state) => {
        const time = state.clock.getElapsedTime();
        starShaderArgs.uniforms.uTime.value = time;

        // Expansion Factor: Extremely slow and gentle (Ghostly Drift)
        const expansion = 1.0 + time * 0.1;

        const positions = new Float32Array(count * 3);
        const linePositions = [];

        // Update Node Positions
        nodes.forEach((node, i) => {
            node.basePos.add(node.vel);

            // Large Bounds for star-field
            const bounds = 10;
            if (Math.abs(node.basePos.x) > bounds) node.vel.x *= -1;
            if (Math.abs(node.basePos.y) > bounds) node.vel.y *= -1;
            if (Math.abs(node.basePos.z) > bounds) node.vel.z *= -1;

            // Apply Gentle Expansion
            node.pos.copy(node.basePos).multiplyScalar(expansion);

            positions[i * 3] = node.pos.x;
            positions[i * 3 + 1] = node.pos.y;
            positions[i * 3 + 2] = node.pos.z;
        });

        if (pointsRef.current) {
            pointsRef.current.geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        }

        // Proximity-based Lines (Faint secondary connections)
        const limit = 4.0;
        for (let i = 0; i < count; i++) {
            for (let j = i + 1; j < count; j++) {
                const dist = nodes[i].pos.distanceTo(nodes[j].pos);
                if (dist < limit) {
                    linePositions.push(nodes[i].pos.x, nodes[i].pos.y, nodes[i].pos.z);
                    linePositions.push(nodes[j].pos.x, nodes[j].pos.y, nodes[j].pos.z);
                }
            }
        }

        if (linesRef.current) {
            linesRef.current.geometry.setAttribute('position', new THREE.Float32BufferAttribute(linePositions, 3));
            linesRef.current.geometry.attributes.position.needsUpdate = true;
        }
    });

    return (
        <group>
            {/* The Points (Star-Like Glowing Nodes with Custom Shader) */}
            <points ref={pointsRef}>
                <bufferGeometry />
                <shaderMaterial
                    args={[starShaderArgs]}
                    transparent
                    blending={THREE.AdditiveBlending}
                    depthWrite={false}
                />
            </points>

            {/* The Lines (Ghostly Faint Connections) */}
            <lineSegments ref={linesRef}>
                <bufferGeometry />
                <lineBasicMaterial
                    color="#2966F4"
                    transparent
                    opacity={0.08}
                    blending={THREE.AdditiveBlending}
                    depthWrite={false}
                />
            </lineSegments>
        </group>
    );
};

// --- Dynamic Loading Status Component ---
const LoadingStatus = () => {
    const [index, setIndex] = useState(0);
    const [dots, setDots] = useState("");
    const statuses = useMemo(() => [
        "MATERIALIZING VISION",
        "CONNECTING POTENTIAL",
        "SYNTHESIZING INTELLIGENCE",
        "ALIGNING NEURAL NETWORKS"
    ], []);

    useEffect(() => {
        const statusInterval = setInterval(() => {
            setIndex((prev) => (prev + 1) % statuses.length);
        }, 3000);

        const dotsInterval = setInterval(() => {
            setDots((prev) => (prev.length >= 3 ? "" : prev + "."));
        }, 600);

        return () => {
            clearInterval(statusInterval);
            clearInterval(dotsInterval);
        };
    }, [statuses]);

    return (
        <div className="flex flex-col items-center">
            <AnimatePresence mode="wait">
                <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 0.6, y: 0 }}
                    exit={{ opacity: 0, y: -5 }}
                    transition={{ duration: 0.8 }}
                    className="mt-4 text-[11px] font-light tracking-[0.8em] text-white/40 uppercase relative whitespace-nowrap"
                >
                    <span style={{ marginRight: '-0.8em' }}>{statuses[index]}</span>
                    <span className="absolute left-full ml-2 w-8 text-left tracking-normal">{dots}</span>
                </motion.div>
            </AnimatePresence>
        </div>
    );
};

// --- Deep Space Particles ---
const DeepParticles = () => {
    const pointsRef = useRef();
    const count = 600;
    const positions = useMemo(() => {
        const p = new Float32Array(count * 3);
        for (let i = 0; i < count; i++) {
            p[i * 3] = (Math.random() - 0.5) * 40;
            p[i * 3 + 1] = (Math.random() - 0.5) * 40;
            p[i * 3 + 2] = (Math.random() - 0.5) * 20;
        }
        return p;
    }, []);

    useFrame((state) => {
        if (!pointsRef.current) return;
        pointsRef.current.rotation.y = state.clock.getElapsedTime() * 0.01;
    });

    return (
        <points ref={pointsRef}>
            <bufferGeometry>
                <bufferAttribute
                    attach="attributes-position"
                    count={count}
                    array={positions}
                    itemSize={3}
                />
            </bufferGeometry>
            <pointsMaterial size={0.025} color="#2966F4" transparent opacity={0.3} sizeAttenuation />
        </points>
    );
};

const RimLight = () => (
    <pointLight position={[0, 0, -12]} intensity={3} color="#ffffff" />
);

// --- Logo Glow / Lighting ---
const BrandingLights = () => {
    return (
        <>
            <ambientLight intensity={0.4} color="#dbeafe" />

            {/* PRIMARY WHITE GLINTS - High Contrast for Total Glass */}
            <directionalLight position={[10, 10, 10]} intensity={3} color="white" />
            <spotLight position={[0, 0, 15]} angle={0.4} penumbra={1} intensity={3.5} color="white" />

            {/* ATMOSPHERIC BLUE LIGHT */}
            <pointLight position={[-8, 5, 5]} intensity={3} color="#2966F4" />
            <pointLight position={[5, -10, -5]} intensity={2.5} color="#2966F4" />

            <RimLight />
        </>
    );
};

const MagicLoader = ({ variant = "network", animationMode = 3 }) => {
    return (
        <motion.div
            initial={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 1.5, ease: "easeInOut" } }}
            className="fixed inset-0 z-[9999] bg-[#020408] overflow-hidden"
        >
            <Canvas dpr={[1, 2]} shadows gl={{ antialias: true }}>
                <PerspectiveCamera makeDefault position={[0, 0, 10]} fov={35} />
                <color attach="background" args={['#020408']} />

                <BrandingLights />
                <Suspense fallback={null}>
                    <Environment preset="night" />

                    <GrainedBackground />
                    <TwinklingStars />

                    <Center>
                        <Float speed={1} rotationIntensity={0.1} floatIntensity={0.3}>
                            {variant === "chains" ? (
                                <>
                                    <SplineChain id={0} count={36} animationMode={animationMode} />
                                    <SplineChain id={1} count={36} animationMode={animationMode} />
                                    <SplineChain id={2} count={36} animationMode={animationMode} />
                                </>
                            ) : (
                                <GraphicalNetwork count={100} />
                            )}
                        </Float>
                    </Center>

                    <DeepParticles />
                </Suspense>
            </Canvas>

            {/* Premium Text Animation Layer */}
            <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none z-20">
                <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 1.5, ease: [0.22, 1, 0.36, 1] }}
                    className="relative"
                >
                    <h1 className="text-7xl md:text-[8rem] font-black text-white tracking-[0.2em] font-heading drop-shadow-[0_0_30px_rgba(255,255,255,0.1)]">
                        IVANI
                    </h1>
                    {/* Animated Light Sweep over text */}
                    <motion.div
                        animate={{ x: ["-100%", "200%"] }}
                        transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/05 to-transparent skew-x-12"
                    />
                </motion.div>

                <LoadingStatus />
            </div>

            {/* Grain & Depth Effects */}
            <div className="absolute inset-0 pointer-events-none opacity-[0.06] bg-noise" />
            <div className="absolute inset-0 pointer-events-none bg-gradient-to-b from-black/40 via-transparent to-black/40" />
        </motion.div>
    );
};

export default MagicLoader;
